import React, { useEffect, useState } from "react";
import "./StockHistorialModal.css";
import {
  getHistorialReciente,
  getHistorialEnRango,
} from "../../../services/stockHistorialService.js";

// Punto de corte: capturas anteriores a esta fecha fueron pruebas.
// NO se borran de la base, solo se ocultan por defecto en la vista.
const FECHA_INICIO_CONTROL_OFICIAL = "2026-08-12";

function fmt(value) {
  return new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(
    Number(value || 0)
  );
}

function fmtFecha(iso) {
  if (!iso) return "N/D";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const INSUMOS_LABELS = {
  plasticos: "Tarjetas",
  ribbon: "Ribbon",
  laminador: "Laminador",
  retransfer: "Retransfer",
  rollos: "Rollo térmico",
};

export default function StockHistorialModal({ open, onClose, oficinaId, oficinaLabel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [capturas, setCapturas] = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [verTodoElHistorico, setVerTodoElHistorico] = useState(false);

  async function cargarReciente() {
    if (!oficinaId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getHistorialReciente(oficinaId);
      const capturasCrudas = data.capturas || [];
      const capturasFiltradas = verTodoElHistorico
        ? capturasCrudas
        : capturasCrudas.filter((c) => {
            const fecha = c.fechaCaptura || c.createdAt;
            return fecha && fecha >= FECHA_INICIO_CONTROL_OFICIAL;
          });
      setCapturas(capturasFiltradas);
    } catch (err) {
      setError(err.message || "No se pudo cargar el historial.");
    } finally {
      setLoading(false);
    }
  }

  async function buscarPorRango() {
    if (!oficinaId || !desde || !hasta) return;
    setLoading(true);
    setError("");
    try {
      const data = await getHistorialEnRango(oficinaId, desde, hasta);
      // La API regresa orden cronológico ascendente para rangos;
      // lo invertimos para mantener "más reciente primero" en la UI.
      setCapturas((data.capturas || []).slice().reverse());
    } catch (err) {
      setError(err.message || "No se pudo cargar el historial en ese rango.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open && oficinaId) {
      setDesde("");
      setHasta("");
      cargarReciente();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, oficinaId, verTodoElHistorico]);

  if (!open) return null;

  return (
    <div className="stock-historial-overlay" role="dialog" aria-modal="true">
      <div className="stock-historial-panel">
        <header className="stock-historial-header">
          <div>
            <span className="stock-historial-kicker">TRAZABILIDAD DE STOCK</span>
            <h2>{oficinaLabel || oficinaId}</h2>
            <p>{capturas.length} captura(s) mostradas</p>
          </div>
          <button type="button" className="stock-historial-close" onClick={onClose}>
            ×
          </button>
        </header>

        {!verTodoElHistorico && (
          <div className="stock-historial-corte-aviso">
            Mostrando capturas desde el {FECHA_INICIO_CONTROL_OFICIAL} (inicio de control oficial).{" "}
            <button
              type="button"
              className="stock-historial-corte-link"
              onClick={() => setVerTodoElHistorico(true)}
            >
              Ver histórico completo (incluye pruebas)
            </button>
          </div>
        )}

        {verTodoElHistorico && (
          <div className="stock-historial-corte-aviso activo">
            Mostrando histórico completo, incluyendo pruebas anteriores al {FECHA_INICIO_CONTROL_OFICIAL}.{" "}
            <button
              type="button"
              className="stock-historial-corte-link"
              onClick={() => setVerTodoElHistorico(false)}
            >
              Volver a vista filtrada
            </button>
          </div>
        )}

        <section className="stock-historial-filtros">
          <label>
            <span>Desde</span>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </label>
          <label>
            <span>Hasta</span>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </label>
          <button
            type="button"
            disabled={!desde || !hasta || loading}
            onClick={buscarPorRango}
          >
            Buscar en rango
          </button>
          <button type="button" className="secondary" onClick={cargarReciente} disabled={loading}>
            Últimas 30
          </button>
        </section>

        {error && <div className="stock-historial-error">{error}</div>}

        <div className="stock-historial-timeline">
          {loading ? (
            <div className="stock-historial-loading">Cargando...</div>
          ) : capturas.length === 0 ? (
            <div className="stock-historial-empty">Sin capturas en este periodo.</div>
          ) : (
            capturas.map((captura, index) => {
              const stock = captura.stock || {};
              const anterior = capturas[index + 1];
              const stockAnterior = anterior ? anterior.stock || {} : null;

              return (
                <article className="stock-historial-item" key={captura.capturaId}>
                  <div className="stock-historial-item-fecha">
                    <strong>{fmtFecha(captura.fechaCaptura || captura.createdAt)}</strong>
                    <span>{captura.responsable || "N/D"}</span>
                  </div>

                  <div className="stock-historial-item-grid">
                    {Object.entries(INSUMOS_LABELS).map(([key, label]) => {
                      const valorActual = Number(stock[key] || 0);
                      const valorAnterior = stockAnterior
                        ? Number(stockAnterior[key] || 0)
                        : null;
                      const delta =
                        valorAnterior !== null ? valorActual - valorAnterior : null;

                      return (
                        <div className="stock-historial-cell" key={key}>
                          <span>{label}</span>
                          <strong>{fmt(valorActual)}</strong>
                          {delta !== null && delta !== 0 && (
                            <small className={delta > 0 ? "up" : "down"}>
                              {delta > 0 ? "+" : ""}
                              {fmt(delta)}
                            </small>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
